import type { CSSProperties, ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  padding?: number | string
  style?: CSSProperties
}
export function Card({ children, padding = 'var(--cp-sp-6)', style }: CardProps) {
  return (
    <div
      style={{
        background: 'var(--cp-bg-elevated)',
        border: '1px solid var(--cp-rule)',
        borderRadius: 'var(--cp-radius-xl)',
        padding,
        boxShadow: 'var(--cp-shadow-sm)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  fullWidth?: boolean
  children: ReactNode
}
export function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  loading = false,
  onClick,
  fullWidth = false,
  children,
}: ButtonProps) {
  const isDisabled = disabled || loading
  const padding =
    size === 'lg' ? '14px 22px' : size === 'sm' ? '6px 12px' : '10px 16px'
  const fontSize =
    size === 'lg' ? 'var(--cp-text-md)' : size === 'sm' ? 'var(--cp-text-sm)' : 'var(--cp-text-base)'

  const base: CSSProperties = {
    padding,
    fontSize,
    fontWeight: 500,
    borderRadius: 'var(--cp-radius-md)',
    border: '1px solid transparent',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all var(--cp-dur-fast) var(--cp-ease-out)',
    opacity: isDisabled ? 0.6 : 1,
    width: fullWidth ? '100%' : undefined,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  }
  let style: CSSProperties
  if (variant === 'primary') {
    style = {
      ...base,
      background: 'var(--cp-accent-primary)',
      color: '#fff',
    }
  } else if (variant === 'secondary') {
    style = {
      ...base,
      background: 'var(--cp-bg-secondary)',
      color: 'var(--cp-fg-primary)',
      borderColor: 'var(--cp-rule)',
    }
  } else {
    style = {
      ...base,
      background: 'transparent',
      color: 'var(--cp-fg-secondary)',
    }
  }
  return (
    <button type={type} disabled={isDisabled} onClick={onClick} style={style}>
      {loading && <Spinner />}
      {children}
    </button>
  )
}

export function Spinner({ size = 14 }: { size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        border: '2px solid currentColor',
        borderRightColor: 'transparent',
        borderRadius: '50%',
        animation: 'cp-spin 0.7s linear infinite',
      }}
    />
  )
}

interface FieldProps {
  label: string
  hint?: ReactNode
  error?: string
  children: ReactNode
}
export function Field({ label, hint, error, children }: FieldProps) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 'var(--cp-text-sm)',
          fontWeight: 500,
          color: 'var(--cp-fg-secondary)',
        }}
      >
        {label}
      </span>
      {children}
      {error ? (
        <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-danger)' }}>{error}</span>
      ) : hint ? (
        <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>{hint}</span>
      ) : null}
    </label>
  )
}

interface TextInputProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoComplete?: string
  inputMode?: 'text' | 'email' | 'numeric'
  spellCheck?: boolean
  disabled?: boolean
  maxLength?: number
  invalid?: boolean
}
export function TextInput({
  value,
  onChange,
  placeholder,
  autoComplete,
  inputMode = 'text',
  spellCheck,
  disabled,
  maxLength,
  invalid,
}: TextInputProps) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      inputMode={inputMode}
      spellCheck={spellCheck}
      disabled={disabled}
      maxLength={maxLength}
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--cp-radius-md)',
        border: `1px solid ${invalid ? 'var(--cp-danger)' : 'var(--cp-rule)'}`,
        background: disabled ? 'var(--cp-bg-code)' : 'var(--cp-bg-secondary)',
        outline: 'none',
        transition: 'border-color var(--cp-dur-fast)',
      }}
    />
  )
}

export function Badge({
  variant = 'neutral',
  children,
}: {
  variant?: 'neutral' | 'success' | 'warning' | 'info' | 'danger'
  children: ReactNode
}) {
  const colours: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: 'var(--cp-bg-code)', fg: 'var(--cp-fg-secondary)' },
    success: { bg: 'var(--cp-success-bg)', fg: 'var(--cp-success-dark)' },
    warning: { bg: 'var(--cp-warning-bg)', fg: 'var(--cp-warning-dark)' },
    info: { bg: 'var(--cp-info-bg)', fg: '#0E7490' },
    danger: { bg: 'var(--cp-danger-bg)', fg: 'var(--cp-danger-dark)' },
  }
  const c = colours[variant]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 'var(--cp-radius-full)',
        fontSize: 'var(--cp-text-xs)',
        fontWeight: 500,
        background: c.bg,
        color: c.fg,
      }}
    >
      {children}
    </span>
  )
}

export function Page({
  children,
  width = 480,
}: {
  children: ReactNode
  width?: number
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'var(--cp-sp-6) var(--cp-sp-4)',
      }}
    >
      <div style={{ width: '100%', maxWidth: width, display: 'flex', flexDirection: 'column', gap: 'var(--cp-sp-4)' }}>
        {children}
      </div>
    </div>
  )
}

export function Header({
  surveyName,
  sponsorName,
  badge,
}: {
  surveyName?: string
  sponsorName?: string
  badge?: ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        animation: 'cp-fadeUp var(--cp-dur-normal) var(--cp-ease-out)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'var(--cp-font-editorial)', fontSize: 'var(--cp-text-xl)' }}>
          ClinPay
        </span>
        {badge}
      </div>
      {surveyName && (
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--cp-text-2xl)',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {surveyName}
        </h1>
      )}
      {sponsorName && (
        <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
          Sponsored by {sponsorName}
        </div>
      )}
    </div>
  )
}

export function Footer({ surveyId }: { surveyId?: string }) {
  return (
    <div
      style={{
        marginTop: 'var(--cp-sp-8)',
        paddingTop: 'var(--cp-sp-4)',
        borderTop: '1px solid var(--cp-rule-subtle)',
        fontSize: 'var(--cp-text-xs)',
        color: 'var(--cp-fg-muted)',
        textAlign: 'center',
        lineHeight: 1.6,
      }}
    >
      Powered by{' '}
      <a href="https://asterpay.io" target="_blank" rel="noreferrer noopener">
        AsterPay
      </a>
      , an EMI-licensed payments partner.
      <br />
      Your data is processed for the purpose of this payment only. See{' '}
      <a href="https://asterpay.io/privacy" target="_blank" rel="noreferrer noopener">
        privacy notice
      </a>
      .
      {surveyId && (
        <>
          <br />
          <span className="cp-mono" style={{ opacity: 0.6 }}>
            survey · {surveyId.slice(0, 8)}
          </span>
        </>
      )}
    </div>
  )
}

export function ErrorPanel({ title, body }: { title: string; body: ReactNode }) {
  return (
    <Card style={{ background: 'var(--cp-danger-bg)', borderColor: 'var(--cp-danger)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 600, color: 'var(--cp-danger-dark)' }}>{title}</div>
        <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-primary)' }}>{body}</div>
      </div>
    </Card>
  )
}

export function FullPageLoader() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--cp-fg-muted)',
        gap: 10,
      }}
    >
      <Spinner /> Loading...
    </div>
  )
}
